<script setup>
/*
------------------------------------------------------------------------
Base Modal Panel Component

A slide-in panel modal that appears from the right edge of the screen.
Provides consistent behavior for all panel-style modals:
- Escape key to close (or custom handler)
- Body scroll lock
- Backdrop click to close (optional)
- Smooth slide-right transition (300ms ease-in-out)
- Smooth fade transition for backdrop
- Focus trap (Tab/Shift+Tab cycles within modal)

Uses a `show` prop pattern for smooth enter AND exit transitions.
The component stays mounted and controls visibility internally.

@slot default - The modal content
@prop {boolean} show - Controls modal visibility (required)
@prop {Function} onClose - Required callback when modal should close
@prop {Function} onEscape - Optional custom escape key handler (defaults to onClose)
@prop {boolean} showBackdrop - Whether to show backdrop (default: true)
@prop {string} maxWidth - Max width class (default: 'max-w-[1000px]')
@prop {boolean} showCloseButton - Whether to show the X close button (default: true)
@prop {boolean} topmost - Display above all other elements including footer (default: false)
@emits after-leave - Emitted when close transition completes
------------------------------------------------------------------------
*/
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from "vue";
import { PhX } from "@phosphor-icons/vue";

const props = defineProps({
	show: {
		type: Boolean,
		required: true,
	},
	onClose: {
		type: Function,
		required: true,
	},
	onEscape: {
		type: Function,
		default: null,
	},
	showBackdrop: {
		type: Boolean,
		default: true,
	},
	closeOnBackdropClick: {
		type: Boolean,
		default: true,
	},
	maxWidth: {
		type: String,
		default: "max-w-[1000px]",
	},
	showCloseButton: {
		type: Boolean,
		default: true,
	},
	topmost: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(["after-leave"]);

const panelRef = ref(null);
let previouslyFocusedElement = null;

const FOCUSABLE_SELECTORS = [
	"button:not([disabled])",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"a[href]",
	'[tabindex]:not([tabindex="-1"])',
].join(", ");

const getFocusableElements = () => {
	if (!panelRef.value) return [];
	return Array.from(panelRef.value.querySelectorAll(FOCUSABLE_SELECTORS));
};

const backdropZIndex = computed(() => (props.topmost ? "z-[9998]" : "z-[140]"));
const panelZIndex = computed(() => (props.topmost ? "z-[9999]" : "z-[150]"));
const closeButtonZIndex = computed(() =>
	props.topmost ? "z-[10000]" : "z-[160]",
);

/*
------------------------------------------------------------------------
Keyboard event handling
------------------------------------------------------------------------
*/
const handleKeydown = (event) => {
	if (!props.show) return;

	if (event.key === "Escape") {
		if (props.onEscape) {
			props.onEscape();
		} else {
			props.onClose();
		}
		return;
	}

	if (event.key === "Tab") {
		const focusableElements = getFocusableElements();
		if (focusableElements.length === 0) {
			event.preventDefault();
			return;
		}

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if (event.shiftKey) {
			if (document.activeElement === firstElement) {
				event.preventDefault();
				lastElement.focus();
			}
		} else {
			if (document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		}
	}
};

/*
------------------------------------------------------------------------
Body scroll lock management
------------------------------------------------------------------------
*/
const lockBodyScroll = () => {
	const scrollbarWidth =
		window.innerWidth - document.documentElement.clientWidth;
	document.body.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
	document.body.classList.add("no-scroll");
};

const unlockBodyScroll = () => {
	document.body.classList.remove("no-scroll");
	document.body.style.removeProperty("--scrollbar-width");
};

/*
------------------------------------------------------------------------
Transition callbacks
------------------------------------------------------------------------
*/
const handleAfterEnter = async () => {
	await nextTick();
	const focusableElements = getFocusableElements();
	if (focusableElements.length > 0) {
		focusableElements[0].focus();
	} else if (panelRef.value) {
		panelRef.value.focus();
	}
};

const handleAfterLeave = () => {
	unlockBodyScroll();
	emit("after-leave");

	// Restore focus to previously focused element
	if (
		previouslyFocusedElement &&
		typeof previouslyFocusedElement.focus === "function"
	) {
		previouslyFocusedElement.focus();
	}
};

/*
------------------------------------------------------------------------
Watch show prop to manage scroll lock and focus
------------------------------------------------------------------------
*/
watch(
	() => props.show,
	(newValue) => {
		if (newValue) {
			// Opening - save focus and lock scroll
			previouslyFocusedElement = document.activeElement;
			lockBodyScroll();
		}
		// Closing is handled in handleAfterLeave
	},
);

/*
------------------------------------------------------------------------
Lifecycle hooks
------------------------------------------------------------------------
*/
onMounted(() => {
	window.addEventListener("keydown", handleKeydown);

	// If initially shown, lock scroll
	if (props.show) {
		previouslyFocusedElement = document.activeElement;
		lockBodyScroll();
	}
});

onUnmounted(() => {
	window.removeEventListener("keydown", handleKeydown);
	// Ensure scroll is unlocked if component unmounts while open
	if (props.show) {
		unlockBodyScroll();
	}
});
</script>

<template>
	<div class="base-modal-panel">
		<!-- Backdrop with fade transition -->
		<Transition name="fade">
			<div
				v-if="show && showBackdrop"
				class="fixed inset-0 bg-black/80"
				:class="backdropZIndex"
				@click="closeOnBackdropClick ? onClose() : null"
			></div>
		</Transition>

		<!-- Panel with slide-right transition -->
		<Transition
			name="slide-right"
			@after-enter="handleAfterEnter"
			@after-leave="handleAfterLeave"
		>
			<div
				v-if="show"
				ref="panelRef"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				class="lux-modal fixed bottom-0 left-0 right-0 top-0 flex flex-col overflow-hidden bg-white text-black shadow-lg focus:outline-none lg:left-auto lg:w-[1000px]"
				:class="[panelZIndex]"
			>
				<slot></slot>

				<!-- Close Button -->
				<button
					v-if="showCloseButton"
					type="button"
					id="modal_close"
					class="spin-effect absolute left-auto right-3 top-3 h-[40px] w-[40px] focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
					:class="closeButtonZIndex"
					@click="onClose"
				>
					<PhX
						:size="31"
						aria-hidden="true"
						class="lux-pos-absmiddle text-blacka75 hover:text-black"
					/>
				</button>
			</div>
		</Transition>
	</div>
</template>
